import {ArrowLeftIcon, XIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {Dialog, FeatureFlags} from '@primer/react/experimental'
import {memo, useEffect, useCallback, useMemo, useRef, useState, type ReactNode} from 'react'
import {useLocation} from '@github-ui/react-router'
import type {PageLimits} from '../../page-data/payloads/files'
import {usePullRequestBasePath} from '../../stores/selectors'
import {
  useCommentsPanelDetailThreadId,
  useCommentsPanelViewActions,
  useCommentsPanelViewMode,
  useLayoutAuthorLogin,
  useLayoutCanComment,
} from '../../stores/layout-selectors'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {createPortal} from 'react-dom'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useDockedPanelMetadata,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './docked-side-panel-constants'
import {Panel} from './Panel'
import {CompactPanel} from './CompactPanel'
import {getPanelHeading} from './panel-registry'
import {CommentsViewControls} from '../comments/CommentsViewControls'
import {Comments} from '../comments/Comments'
import {useCommentsFilters} from '../comments/use-comments-filters'
import {useCommentsData} from '../comments/use-comments-data'
import {useIssueCommentLiveUpdates} from '../../hooks/use-issue-comment-live-updates'
import {isIssueCommentComposerEnabled} from '../comments/IssueCommentComposer'
import {CommentsContext} from '../comments/CommentsContext'
import {CommentsSidePanelFooter, type CommentsSidePanelFooterHandle} from './CommentsSidePanelFooter'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export interface CommentsSidePanelProps {
  pageLimits?: PageLimits
}

/**
 *
 * Renders a sidesheet displaying previews of the pull request's threads or issue comments.
 *
 * Self-contained: sources its own data from hooks/context.
 *
 * Rendering target depends on the `updated_pull_request_header` flag:
 * - **ON**: rendered by `DockedSidePanelContent` and emits `<Panel>` inline; that
 *   parent already lives inside the docked sidebar.
 * - **OFF**: rendered as a child of `OpenCommentsPanelButton` (to inherit
 *   `PullRequestStoreProvider` scope needed by `ThreadPositioningWithStore`),
 *   and portals `<Panel>` into `DOCKED_SIDE_PANEL_CONTENT_ID` so it visually
 *   appears in the sidebar.
 *
 * In compact mode, falls back to a Dialog sidesheet regardless of flag.
 */
export const CommentsSidePanel = memo(function CommentsSidePanelComponent({pageLimits}: CommentsSidePanelProps) {
  const isOpen = useIsDockedPanelOpen(DockedPanel.Comments)
  const isCompact = useIsDockedPanelCompact()
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  const pathName = usePullRequestBasePath()
  const pullRequestAuthorLogin = useLayoutAuthorLogin()
  const canComment = useLayoutCanComment()

  // The panel's view state lives in the layout store so it survives this component unmounting
  // when the panel closes. It is mapped onto `CommentsContext` here so the comments subtree
  // stays reusable by consumers that supply their own state instead.
  const viewMode = useCommentsPanelViewMode()
  const detailThreadId = useCommentsPanelDetailThreadId()
  const {setCommentsPanelViewMode, setCommentsPanelDetailThreadId} = useCommentsPanelViewActions()

  const commentsContextValue = useMemo(
    () => ({
      viewMode,
      setViewMode: setCommentsPanelViewMode,
      detailThreadId,
      setDetailThreadId: setCommentsPanelDetailThreadId,
    }),
    [viewMode, detailThreadId, setCommentsPanelViewMode, setCommentsPanelDetailThreadId],
  )

  const {
    threadPreviews,
    totalDiffCommentCount,
    conversationItems,
    mergedConversationComments,
    threadPreviewsAuthorList,
    issueCommentsAuthorList,
  } = useCommentsData()

  const {
    threadPreviewFilterState,
    setThreadPreviewFilterState,
    issueCommentsFilterState,
    setIssueCommentsFilterState,
    filteredThreadIds,
    filteredIssueCommentIds,
    selectedReviewId,
    applyFilterOverrides,
  } = useCommentsFilters({threadPreviews, conversationComments: mergedConversationComments})

  // Open panel and apply filter from URL review_id param
  const {openPanel, closePanel: closePanelAction} = useDockedPanelActions()
  const location = useLocation()
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const reviewId = urlParams.get('review_id')

    if (!reviewId) return
    const showResolved = urlParams.get('show_resolved')
    const resolvedFilter = showResolved === 'true' ? ' is:resolved' : showResolved === 'false' ? ' is:unresolved' : ''
    setThreadPreviewFilterState(prev => ({
      ...prev,
      filterText: `review-id:${reviewId}${resolvedFilter}`.trim(),
    }))
    openPanel(DockedPanel.Comments)

    return () => {
      closePanelAction()
    }
  }, [location.pathname, location.search, openPanel, closePanelAction, setThreadPreviewFilterState])

  // Apply panel metadata (e.g. author filter from action items) during render to avoid a
  // double-render from an effect. Tracks the last applied metadata object so the update
  // only fires when metadata changes while the panel is open.
  const panelMetadata = useDockedPanelMetadata()
  const [lastAppliedMetadata, setLastAppliedMetadata] = useState(panelMetadata)
  if (isOpen && panelMetadata && panelMetadata !== lastAppliedMetadata) {
    setLastAppliedMetadata(panelMetadata)

    // Switch view mode if specified in metadata
    if (panelMetadata.viewMode) {
      setCommentsPanelViewMode(panelMetadata.viewMode)
    }

    applyFilterOverrides(panelMetadata)
  }

  const inThreadDetailMode = detailThreadId && threadPreviews.some(thread => thread.id === detailThreadId)

  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const footerRef = useRef<CommentsSidePanelFooterHandle>(null)
  const handleQuoteReply = useCallback((quotedText: string) => footerRef.current?.quoteReply(quotedText), [])

  const handleBackToThreadList = useCallback(() => {
    setCommentsPanelDetailThreadId(undefined)
  }, [setCommentsPanelDetailThreadId])

  const isCommentPanelFooterEnabled = isIssueCommentComposerEnabled(viewMode, canComment)
  const commentsFooter = isCommentPanelFooterEnabled ? <CommentsSidePanelFooter ref={footerRef} /> : null
  // Legacy compact Dialog renders the footer without the css-anchor-positioning
  // feature flag wrapper — preserve that to avoid behavior change.
  const commentPanelFooter = commentsFooter ? <Dialog.Footer>{commentsFooter}</Dialog.Footer> : null

  const limitExceeded = pageLimits?.reviewThreadsPaginatedLimitExceeded
  const limitCount = pageLimits?.reviewThreadsPaginatedLimit

  const updatedPullRequestHeader = isFeatureEnabled('updated_pull_request_header')

  // Subscribe to alive events for live comment updates from other users.
  // This must be above the `if (!isOpen)` guard so the subscription stays
  // active even when the panel is closed. The hook self-activates only
  // once comments have been loaded into the store.
  useIssueCommentLiveUpdates()

  if (!isOpen) return null

  const withCommentsContext = (children: ReactNode) => (
    <CommentsContext value={commentsContextValue}>{children}</CommentsContext>
  )

  if (!isCompact) {
    const panelContent = (
      <Panel panelName={DockedPanel.Comments}>
        <Panel.Header>
          {inThreadDetailMode ? (
            <div className="py-2">
              <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
                Back to all comments
              </Button>
            </div>
          ) : (
            <CommentsViewControls
              threadPreviewsAuthorList={threadPreviewsAuthorList}
              issueCommentsAuthorList={issueCommentsAuthorList}
              threadPreviewFilterState={threadPreviewFilterState}
              setThreadPreviewFilterState={setThreadPreviewFilterState}
              issueCommentsFilterState={issueCommentsFilterState}
              setIssueCommentsFilterState={setIssueCommentsFilterState}
              iconOnly
            />
          )}
        </Panel.Header>
        <Panel.Body>
          <Comments
            filteredThreadIds={filteredThreadIds}
            filteredIssueCommentIds={filteredIssueCommentIds}
            selectedReviewId={selectedReviewId}
            conversationItems={conversationItems}
            handleQuoteReply={handleQuoteReply}
            pathName={pathName}
            pullRequestAuthorLogin={pullRequestAuthorLogin}
            limitExceeded={limitExceeded}
            threadPreviews={threadPreviews}
            totalDiffCommentCount={totalDiffCommentCount}
            limitCount={limitCount}
          />
        </Panel.Body>
        {isCommentPanelFooterEnabled && (
          <Panel.Footer>
            <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>{commentsFooter}</FeatureFlags>
          </Panel.Footer>
        )}
      </Panel>
    )

    // When the updated_pull_request_header flag is OFF, the panel is rendered
    // as a child of OpenCommentsPanelButton (so it inherits PullRequestStoreProvider
    // scope needed by ThreadPositioningWithStore). It must portal into the
    // central docked-panel target div so it visually appears in the sidebar.
    // When the flag is ON, the panel is rendered directly inside that target div
    // by DockedSidePanelContent and no portal is needed.
    if (!updatedPullRequestHeader) {
      const portalTarget = ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID)
      if (portalTarget) {
        return createPortal(withCommentsContext(panelContent), portalTarget)
      }
      return null
    }

    return withCommentsContext(panelContent)
  }

  // Flag-off compact carve-out: CompactSidePanelDialog skips Comments under
  // !updated_pull_request_header, so keep the legacy per-panel Dialog here.
  const compactCommentsBody = (
    <Comments
      limitExceeded={limitExceeded}
      limitCount={limitCount}
      conversationItems={conversationItems}
      onNavigateToDiffComment={closePanel}
      filteredThreadIds={filteredThreadIds}
      filteredIssueCommentIds={filteredIssueCommentIds}
      handleQuoteReply={handleQuoteReply}
      pathName={pathName}
      selectedReviewId={selectedReviewId}
      threadPreviews={threadPreviews}
      totalDiffCommentCount={totalDiffCommentCount}
      pullRequestAuthorLogin={pullRequestAuthorLogin}
    />
  )

  if (!updatedPullRequestHeader) {
    return withCommentsContext(
      <Dialog
        initialFocusRef={closeButtonRef}
        position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
        returnFocusRef={returnFocusRef}
        onClose={closePanel}
        renderFooter={() => {
          return commentPanelFooter
        }}
        renderHeader={({dialogLabelId}) => (
          <Dialog.Header>
            <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
              {inThreadDetailMode ? (
                <>
                  <span className="sr-only" id={dialogLabelId}>
                    {getPanelHeading(DockedPanel.Comments)}
                  </span>
                  <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
                    Back to all comments
                  </Button>
                </>
              ) : (
                <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.Comments)}</Dialog.Title>
              )}
              <IconButton
                ref={closeButtonRef}
                aria-label="Close comments panel"
                icon={XIcon}
                variant="invisible"
                onClick={closePanel}
              />
            </div>
            {!inThreadDetailMode ? (
              <CommentsViewControls
                threadPreviewsAuthorList={threadPreviewsAuthorList}
                issueCommentsAuthorList={issueCommentsAuthorList}
                threadPreviewFilterState={threadPreviewFilterState}
                setThreadPreviewFilterState={setThreadPreviewFilterState}
                issueCommentsFilterState={issueCommentsFilterState}
                setIssueCommentsFilterState={setIssueCommentsFilterState}
                iconOnly
              />
            ) : null}
          </Dialog.Header>
        )}
      >
        {compactCommentsBody}
      </Dialog>,
    )
  }

  // Flag-on compact: portal full content into the shared CompactSidePanelDialog.
  return withCommentsContext(
    <>
      <CompactPanel.Header>
        {inThreadDetailMode ? (
          <Button leadingVisual={ArrowLeftIcon} variant="invisible" onClick={handleBackToThreadList}>
            Back to all comments
          </Button>
        ) : (
          <CommentsViewControls
            threadPreviewsAuthorList={threadPreviewsAuthorList}
            issueCommentsAuthorList={issueCommentsAuthorList}
            threadPreviewFilterState={threadPreviewFilterState}
            setThreadPreviewFilterState={setThreadPreviewFilterState}
            issueCommentsFilterState={issueCommentsFilterState}
            setIssueCommentsFilterState={setIssueCommentsFilterState}
            iconOnly
          />
        )}
      </CompactPanel.Header>
      <CompactPanel.Content>{compactCommentsBody}</CompactPanel.Content>
      {isCommentPanelFooterEnabled && (
        <CompactPanel.Footer>
          <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>{commentsFooter}</FeatureFlags>
        </CompactPanel.Footer>
      )}
    </>,
  )
})
